/**
 * Canonical forms for user-supplied text.
 *
 * Lives under supabase/functions/_shared so that the Edge Functions (Deno) and
 * the browser bundle (Vite, via the `@shared` alias) run the *same* code rather
 * than two copies that drift. Keep it dependency-free and Deno-compatible:
 * relative imports need explicit .ts extensions, and no `@/` path aliases.
 *
 * These mirror `normalize_text`, `normalize_email` and `normalize_phone` in
 * migration 0001 exactly. The database is authoritative — it normalises on the
 * way in via triggers regardless of what the client sends — but the client
 * applies the same rules so that what the user sees before submitting is what
 * gets stored, and so that comparisons in the import preview line up with the
 * comparisons the database will make.
 *
 * If you change one side, change the other, and add a case to normalize.test.ts.
 */

/** Trim, collapse internal whitespace runs, and map empty to null. */
export function normalizeText(value: string | null | undefined): string | null {
  if (value == null) return null
  const collapsed = value.trim().replace(/\s+/g, ' ')
  return collapsed === '' ? null : collapsed
}

/** Trim and lowercase. Empty becomes null. */
export function normalizeEmail(value: string | null | undefined): string | null {
  if (value == null) return null
  const trimmed = value.trim().toLowerCase()
  return trimmed === '' ? null : trimmed
}

/**
 * Keep digits, preserving a single leading `+`. Everything a person might type
 * around a number — spaces, dashes, parentheses — is discarded, so
 * "(555) 010-9999" and "5550109999" compare equal.
 */
export function normalizePhone(value: string | null | undefined): string | null {
  if (value == null) return null
  const trimmed = value.trim()
  const digits = trimmed.replace(/[^0-9]/g, '')
  if (digits === '') return null
  return trimmed.startsWith('+') ? `+${digits}` : digits
}

/** Free-form but lowercased, matching the attendees.gender CHECK constraint. */
export function normalizeGender(value: string | null | undefined): string | null {
  const text = normalizeText(value)
  return text === null ? null : text.toLowerCase()
}

/** A matchmaking ID is exactly three digits; `7` and `07` mean `007`. */
export function normalizeMatchmakingId(value: string | null | undefined): string | null {
  if (value == null) return null
  const digits = value.trim().replace(/[^0-9]/g, '')
  if (digits === '' || digits.length > 3) return null
  return digits.padStart(3, '0')
}
